import React from "react";
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import TuneIcon from "@mui/icons-material/Tune";
import TranslateIcon from "@mui/icons-material/Translate";
import FlagIcon from "@mui/icons-material/Flag";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ReasonSubreasonModuleEnum from "../../enums/ReasonSubreasonModuleEnum";
import { ActionTypeEnum } from "../../enums/ActionTypeEnum";

export type ReasonSubreasonFormValue = {
    reason?: string;
    subReason?: string;
    clientId?: number | string;
    module?: string;
    actionType?: string;
    isActive?: boolean;
    isVisible?: boolean;
    shouldCreateEscalation?: boolean;
    markLeave?: boolean;
    refundPercentage?: number | string | null;
    cookPenaltyAmount?: number | string | null;
    internalRatingParamId?: number | string | null;
    trHi?: string;
    trBn?: string;
};

type Props = {
    mode: "create" | "edit";
    value: ReasonSubreasonFormValue;
    onChange: (key: keyof ReasonSubreasonFormValue, value: any) => void;
    onSubmit: () => void;
    onCancel: () => void;
    submitting?: boolean;
};

const SectionCard = ({
    icon,
    title,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            border: "1px solid #e8ebed",
            borderRadius: 2,
            mb: 2,
        }}
    >
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
            {icon}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {children}
    </Paper>
);

const moduleOptions = Object.entries(ReasonSubreasonModuleEnum).map(([k, v]) => ({
    label: k.replaceAll("_", " "),
    value: v as string,
}));

const actionTypeOptions = Object.entries(ActionTypeEnum).map(([k, v]) => ({
    label: k.replaceAll("_", " "),
    value: v as string,
}));

const clientIdOptions = [
    { label: "Centaurus", value: 2 },
    { label: "Customer App", value: 4 },
    { label: "Website", value: 9 },
];

const ReasonSubreasonFormFields: React.FC<Props> = ({
    mode,
    value,
    onChange,
    onSubmit,
    onCancel,
    submitting,
}) => {
    return (
        <Box sx={{ pb: 10 }}>
            <SectionCard icon={<RuleIcon color="primary" />} title="Basic Info">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Reason"
                            fullWidth
                            required
                            value={value.reason ?? ""}
                            onChange={(e) => onChange("reason", e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Sub Reason"
                            fullWidth
                            value={value.subReason ?? ""}
                            onChange={(e) => onChange("subReason", e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Module</InputLabel>
                            <Select
                                label="Module"
                                value={value.module ?? ""}
                                onChange={(e) => onChange("module", e.target.value)}
                            >
                                {moduleOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Action Type</InputLabel>
                            <Select
                                label="Action Type"
                                value={value.actionType ?? ""}
                                onChange={(e) => onChange("actionType", e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {actionTypeOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Client</InputLabel>
                            <Select
                                label="Client"
                                value={value.clientId ?? ""}
                                onChange={(e) => onChange("clientId", e.target.value)}
                            >
                                {clientIdOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label} ({opt.value})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard icon={<TuneIcon color="primary" />} title="Behaviour & Penalties">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Refund %"
                            type="number"
                            fullWidth
                            value={value.refundPercentage ?? ""}
                            onChange={(e) => onChange("refundPercentage", e.target.value)}
                            size="small"
                            inputProps={{ min: 80, max: 100 }}
                            helperText="Between 80 and 100"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Cook Penalty Amount"
                            type="number"
                            fullWidth
                            value={value.cookPenaltyAmount ?? ""}
                            onChange={(e) => onChange("cookPenaltyAmount", e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Internal Rating Param Id"
                            type="number"
                            fullWidth
                            value={value.internalRatingParamId ?? ""}
                            onChange={(e) => onChange("internalRatingParamId", e.target.value)}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard icon={<TranslateIcon color="primary" />} title="Translations">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Hindi"
                            fullWidth
                            value={value.trHi ?? ""}
                            onChange={(e) => onChange("trHi", e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Bangla"
                            fullWidth
                            value={value.trBn ?? ""}
                            onChange={(e) => onChange("trBn", e.target.value)}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard icon={<FlagIcon color="primary" />} title="Flags">
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!value.isActive}
                                    onChange={(e) => onChange("isActive", e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!value.isVisible}
                                    onChange={(e) => onChange("isVisible", e.target.checked)}
                                />
                            }
                            label="Visible"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!value.shouldCreateEscalation}
                                    onChange={(e) => onChange("shouldCreateEscalation", e.target.checked)}
                                />
                            }
                            label="Create Escalation"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!value.markLeave}
                                    onChange={(e) => onChange("markLeave", e.target.checked)}
                                />
                            }
                            label="Mark Leave"
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <Paper
                elevation={3}
                sx={{
                    position: "sticky",
                    bottom: 0,
                    mt: 2,
                    p: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    backgroundColor: "#fff",
                    borderTop: "1px solid #e8ebed",
                    zIndex: 5,
                }}
            >
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<CloseIcon />}
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={onSubmit}
                    disabled={submitting}
                >
                    {submitting ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
                </Button>
            </Paper>
        </Box>
    );
};

export default ReasonSubreasonFormFields;
